
import React, { useEffect } from 'react'
import Navigation from '../../../Components/Navigation/Navigation'
import AOS from 'aos'
import 'aos/dist/aos.css'
import petCareMockup from '../../../assets/images/p1.png'
import Footer from '../../../Components/Footer/Footer'
import Hero from './Sections/Hero/Hero'
import IndustryChallengesAndSolutions from './Sections/IndustryChallengesAndSolutions/IndustryChallengesAndSolutions'
import IndustryTech from './Sections/IndustryTech/IndustryTech'
import p2 from '../../../assets/images/p2.png';
import AISearch from './Sections/AISearch/AISearch'
import SpecializedServices from './Sections/SpecializedServices/SpecializedServices'
import WhyChoose from './Sections/WhyChoose/WhyChoose'
import p8 from '../../../assets/images/p8.png';
import CaseStudies from '../../Home/Components/CaseStudies'
import ClientSpeak from '../../Home/Components/ClientSpeak'
import GetQuote from '../../../Components/GetQuote/GetQuote'
import Contect from '../../Home/Components/Contect'
import FAQ from '../../../Components/FAQ/FAQ';

const PetCare = () => {

  useEffect(() => {
    AOS.init({
      duration: 1000,
      once: true,
      offset: 100,
    });
  }, []);

  const homeContent = {
    title: "Why Pet Care Apps Keep Failing (And How We Fix Them)",
    description: "From emergency-ready mobile platforms that save lives to wellness systems that predict health issues before they become expensive emergencies, we build solutions that keep both pets healthier and owners completely loyal to your brand.",
    image: petCareMockup,
    breadcrumb: "Pet Care",
    b: "BOOK A FREE CONSULTATION"
  }

  const industryChallengesAndSolutionsContent = {
    challengesTitle: "The Hidden Crisis Killing Pet Care Apps",
    solutionsTitle: "The Solutions That Actually Keep Pet Owners Coming Back",
    challenges: [
      {
        title: "Pet Owners Can't Find What They Need When Emergencies Strike",
        description: "When Fluffy stops breathing at midnight, generic pet apps become useless digital paperweights that waste precious minutes while your customers desperately search for real help.",
        icon: '/images/industries/petcare/0.svg',
      },
      {
        title: "Your Pet App Security Is Weaker Than You Think",
        description: "With pet health data breaches exposing sensitive veterinary records, your current protections might be the vulnerability that destroys customer trust and triggers devastating legal consequences.",
        icon: '/images/industries/petcare/1.svg',
      },
      {
        title: 'Most Pet Apps Bore Users to Death Within Days',
        description: "Those static reminder apps aren't just ignored, they're actively deleted by frustrated pet parents who expected engaging experiences but got glorified calendar notifications instead.",
        icon: '/images/industries/petcare/2.svg',
      },
      {
        title: 'Rural Pet Owners Get Left Behind by City-Focused Apps',
        description: 'If your app assumes high-speed internet and nearby specialists, you\'re abandoning the 60 million rural pet owners who need digital solutions most desperately.',
        icon: '/images/industries/petcare/3.svg',
      },
      {
        title: 'Your Pet Tech Stack Is Falling Apart',
        description: 'Disconnected systems create dangerous gaps where critical pet health information gets lost between platforms, risking lives and exposing you to malpractice liability.',
        icon: '/images/industries/petcare/4.svg',
      },
      {
        title: 'Pet Products Easily Not Available Everywhere',
        description: "When pet owners can't find specialty food or medications locally, they abandon your app and find competitors who deliver what their pets need, exactly when they need it.",
        icon: '/images/industries/petcare/5.svg',
      },
    ],
    solutions: [
      {
        title: 'Emergency-Ready Platforms That Save Lives',
        description: 'We build instant-access emergency features with 24/7 veterinary connections, transforming terrifying pet crises into manageable situations.',
        icon: '/images/industries/petcare/6.svg',
      },
      {
        title: 'Fort Knox-Level Security That Pet Owners Trust',
        description: 'Our military-grade encryption and HIPAA-compliant infrastructure creates an unbreachable vault for sensitive pet data.',
        icon: '/images/industries/petcare/7.svg',
      },
      {
        title: 'Engagement Features That Create Daily Habits',
        description: 'We engineer addictive wellness tracking, gamified care routines, and community features that make pet owners check your app multiple times daily.',
        icon: '/images/industries/petcare/8.svg',
      },
      {
        title: 'Apps That Work Everywhere, From Manhattan to Montana',
        description: 'Our offline-capable systems and rural-optimized features ensure every pet owner gets premium digital care.',
        icon: '/images/industries/petcare/9.svg',
      },
      {
        title: 'Seamless Integration That Just Works',
        description: 'Our API expertise connects your existing veterinary systems, e-commerce platforms, and third-party devices into one unified ecosystem.',
        icon: '/images/industries/petcare/10.svg',
      },
      {
        title: 'We Developed E-commerce Apps for Seamless Fast Delivery',
        description: 'Custom marketplace integration ensures pet parents never face empty food bowls or delayed medications.',
        icon: '/images/industries/petcare/11.svg',
      },
    ]
  }

  const industryTechContent = {
    mainTitle: "The Tech Stack That's Dominating Pet Care Development",
    leftHeading: 'Harnessing Advanced Technologies For Pet Care App Development',
    leftParagraph: 'We believe that integrating cutting-edge technology creates life-saving opportunities and unprecedented convenience for pet care. Our approach builds robust, future-proof pet applications that keep you ahead of the competition.',
    leftImage: p2,
    performanceItems: [
      {
        title: 'Real-Time Video Consultations & Streaming',
        description: 'Clear video teleconferencing that allows veterinarians to remain distant and diagnose pets in an effort to minimize the anxiety in pets.',
        icon: '/images/industries/petcare/12.svg',
      },
      {
        title: 'AI-Powered Health Predictions & Alerts',
        description: 'Machine learning algorithms that analyze pet behavior patterns and biometric data , catching serious health issues weeks before symptoms appear.',
        icon: '/images/industries/petcare/13.svg',
      },
      {
        title: 'Pet Nutrition & Wellness Apps',
        description: 'Smart food recommendations and supplement tracking that maximizes pet health according to breed, age, and activity level, and any current medical conditions.',
        icon: '/images/industries/petcare/14.svg',
      },
      {
        title: 'Pet Trackers & Health Devices',
        description: 'Fully linked to GPS collar, fitness trackers, and smart feeders, whose technology will provide a comprehensive health record and give the owners total assurance.',
        icon: '/images/industries/petcare/15.svg',
      }
    ]
  };

  const aiSearchContent = {
    mainTitle: 'The Complete Pet Care App Suite You\'ve Been Waiting For',
    sections: [
      {
        title: 'AI Veterinary Assistant App',
        description: 'Pet owners require on-demand, quality information at odd hours when their furry companions show troubling signs. Our AI veterinarian triages at an expert level 24/7, reducing unnecessary emergency visits while escalating serious cases instantly.',
        image: '/images/industries/petcare/p3.png',
        imageAlt: 'AI Veterinary Assistant',
        imagePosition: 'left',
        features: [
          'Symptom checker that flags conditions requiring immediate veterinary attention',
          'Individualized treatment programs with step-by-step home care for simple issues',
          'Smart medication tracking with weight-based dosage alerts to prevent mistakes',
          'Breed-specific health warnings for genetic risks before they become expensive problems',
          '24/7 emergency escalation connecting owners with licensed veterinarians instantly',
          'Real-time monitoring that adapts recommendations as symptoms change',
        ],
      },
      {
        title: 'Telehealth Consultation Platform',
        description: 'Distance shouldn\'t determine care quality. Our platform makes remote consultations as thorough and personal as in-office visits, with full medical context for vets and convenience for owners.',
        image: '/images/industries/petcare/p4.png',
        imageAlt: 'Telehealth Consultation Platform',
        imagePosition: 'right',
        features: [
          'Instant appointment booking that eliminates phone calls and scheduling delays',
          'HD video consultations with HIPAA-compliant security for professional exams',
          'Digital prescriptions sent directly to owners to avoid unnecessary office visits',
          'Complete medical record access for veterinarians during calls',
          'Automated follow-up scheduling to ensure proper recovery tracking',
          'Multi-device compatibility across phones, tablets, and desktops',
        ],
      },
      {
        title: 'Smart Pet Health Monitoring System',
        description: 'Proactive monitoring turns random symptoms into predictable patterns, preventing expensive emergencies and ensuring treatment arrives exactly when needed.',
        image: '/images/industries/petcare/p5.png',
        imageAlt: 'Smart Pet Health Monitoring',
        imagePosition: 'left',
        features: [
          'Continuous biometric tracking to catch subtle changes weeks before symptoms',
          'Instant alerts notifying owners and vets when intervention is necessary',
          'Visual trends that clearly show improvement or decline over time',
          'Wearable device integration for comprehensive, low-stress data capture',
          'Predictive analytics forecasting issues based on breed and history',
          'Automated veterinary reports that streamline professional consultations',
        ],
      },
      {
        title: 'Owner Dashboard & Analytics Portal',
        description: 'Centralized records empower smart decisions and better treatment strategies. Our owner portal unifies medical history, spending, and progress into a single, clear system.',
        image: '/images/industries/petcare/p6.png',
        imageAlt: 'Owner Dashboard & Analytics',
        imagePosition: 'right',
        features: [
          'Full health records for vaccinations, treatments, and behavior tracking',
          'Spending analytics that help budget care and identify savings',
          'Progress tracking showing measurable improvements and recovery milestones',
          'Veterinary communication logs for complete consultation histories',
          'Family sharing to keep everyone aligned on pet care responsibilities',
          'Automated reminders for meds, appointments, and important deadlines',
        ],
      },
      {
        title: 'Community & Social Features Platform',
        description: 'Pet ownership thrives in community. Our platform connects owners for support, expert advice, and trusted services nearby.',
        image: '/images/industries/petcare/p7.png',
        imageAlt: 'Community & Social Features',
        imagePosition: 'left',
        features: [
          'Breed-specific support groups for shared health and behavior challenges',
          'Local service directory for groomers, sitters, and emergency vets',
          'Photo sharing and milestone tracking to celebrate progress',
          'Expert Q&A sessions with veterinarians and training specialists',
          'Emergency support networks for immediate help during crises',
          'Success story sharing to inspire and showcase effective care',
        ],
      },
    ],
  };

  const servicesContent = {
    title: 'The Development Services That Separate Winners from Losers',
    cards: [
      {
        icon: '/images/industries/petcare/16.svg',
        title: 'Custom AI Health Algorithms',
        description: 'Machine learning models trained specifically on your pet care approach, delivering personalized health insights that increase treatment success rates dramatically.',
        buttonText: 'Discover More',
      },
      {
        icon: '/images/industries/petcare/17.svg',
        title: 'Veterinary Compliance & Data Protection',
        description: 'HIPAA-level security and veterinary regulation compliance that exceeds every industry standard while maintaining seamless user experiences for pet owners.',
        buttonText: 'Discover More',
      },
      {
        icon: '/images/industries/petcare/18.svg',
        title: 'Cross-Platform Mobile Development',
        description: 'Native iOS and Android applications that perform flawlessly across all devices, ensuring every pet owner accesses your services regardless of technology preferences.',
        buttonText: 'Discover More',
      },
      {
        icon: '/images/industries/petcare/19.svg',
        title: 'IoT Device Integration & APIs',
        description: 'Smooth integration with pet clothing, feeders, cameras, health monitors, etc. that allow building closed pet maintenance systems without breaking a sweat with the tech.',
        buttonText: 'Discover More',
      },
      {
        icon: '/images/industries/petcare/20.svg',
        title: 'Advanced Analytics & Reporting Systems',
        description: 'Transform pet health data into actionable insights that reduce veterinary costs while dramatically improving treatment outcomes and owner satisfaction levels.',
        buttonText: 'Discover More',
      },
    ],
  };

  const whyChooseContent = {
    title: 'Why Smart Pet Companies Choose Our Development Team',
    image: p8,
    imageAlt: 'Why Choose Us for Pet Care App Development',
    items: [
      {
        title: 'Battle-Tested Pet Industry Experience',
        desc: 'Twelve years developing veterinary software that solves your toughest operational challenges and drives measurable growth.',
      },
      {
        title: 'Apps Built Specifically for Your Vision',
        desc: 'Custom platforms designed around your unique pet care philosophy, never forcing restrictive cookie-cutter solutions.',
      },
      {
        title: 'Security Standards That Pass Any Audit',
        desc: 'Military-grade compliance in every solution securing sensitive pet data behind inadmissible encryption protocols.',
      },
      {
        title: 'A Scalable Code',
        desc: 'Your application grows with your user base while staying fast and reliable—without flooding your code with handwritten micro-optimizations.',
      },
      {
        title: 'Developers Who Actually Answer Their Phones',
        desc: 'Dedicated pet industry specialists available 24/7 when technical emergencies threaten your business operations.',
      },
    ],
  };

  const faqContent = {
    title: "Got Questions? We've Got Answers",
    questions: [
      {
        q: "How do you ensure our pet app will actually engage users long-term? ",
        a: "We combine behavioral psychology, gamification, AI personalization, and community features to create pet care experiences that owners use daily, not just during emergencies."
      },
      {
        q: "Do you connect with other veterinary practice management systems?",
        a: "We certainly do, and because seamless PMS integration is our specialty, we safeguard your data and introduce powerful new capabilities that complement all of your existing processes."
      },
      {
        q: "What is your strategy in managing sensitive pet health data?",
        a: "We employ a high level of military encryption, frequent security scans, and the close adherence to the compliance procedures to have the pet medical data of high-security standards and compliant with all legal requirements."
      },
      {
        q: "How much time may a custom pet care app take?",
        a: "Timelines can vary depending on complexity but most comprehensive pet apps are launched in 4-6 months with subsequent features and support available whenever you need."
      }
    ]
  };

  return (
    <div>
      <Navigation />
      <Hero homeContent={homeContent} />
      <div data-aos="fade-up">
        <IndustryChallengesAndSolutions industryChallengesAndSolutionsContent={industryChallengesAndSolutionsContent} />
      </div>
      <div data-aos="fade-up">
        <IndustryTech industryTechContent={industryTechContent} />
      </div>
      <div data-aos="fade-up">
        <AISearch aiSearchContent={aiSearchContent} />
      </div>
      <div data-aos="fade-up">
        <SpecializedServices servicesContent={servicesContent} />
      </div>
      <div data-aos="fade-up">
        <WhyChoose whyChooseContent={whyChooseContent} />
      </div>
      <div data-aos="fade-up">
        <CaseStudies />
      </div>
      <div data-aos="fade-up">
        <ClientSpeak />
      </div>
      <div data-aos="fade-up">
        <GetQuote />
      </div>
      <div data-aos="fade-up">
        <Contect />
      </div>
      <div data-aos="fade-up">
        <FAQ faqContent={faqContent} />
      </div>
      <Footer />
    </div>
  )
}

export default PetCare